import { CONFLICT_USER_COLORS } from '@/shared/config';
import { forwardRef } from 'react';
import type { AvatarProps } from './avatar.types';

/**
 * Renders the user's sidebar avatar as either the uploaded photo or their initials. Uses avatarLink when present, otherwise shows the first characters of the first and last name over a background colour picked from CONFLICT_USER_COLORS by the first name's character code. Forwards its ref and any extra props (e.g. onClick) to the root element so it keeps working as an antd Popover trigger, which clones props directly onto its child.
 *
 * @param className - Class name applied to the avatar container.
 * @param avatarLink - The user's uploaded photo, rendered instead of the initials tile when present.
 * @param firstName - First name supplying the leading initial and the colour seed.
 * @param lastName - Last name supplying the trailing initial.
 * @returns The avatar element, either an image or an initials tile.
 */
const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, avatarLink, firstName, lastName, ...rest }, ref) => {
    const firstCharacterOfFirstName = firstName?.charAt(0);
    const firstCharacterOfLastName = lastName?.charAt(0);
    const charactersInHex = (firstName?.charCodeAt(0) || 0) + (firstName?.charCodeAt(0) || 0);
    const backgroundColor = CONFLICT_USER_COLORS[charactersInHex % CONFLICT_USER_COLORS.length];

    return avatarLink ? (
      <div className={className} ref={ref} {...rest}>
        <img src={avatarLink} alt="avatar" style={{ borderRadius: '50%' }} />
      </div>
    ) : (
      <div
        className={className}
        style={{ backgroundColor, borderRadius: '50%' }}
        ref={ref}
        {...rest}
      >
        <span className="name">{`${firstCharacterOfFirstName}${firstCharacterOfLastName}`}</span>
      </div>
    );
  },
);

export default Avatar;
